import React, { useState, useEffect, useRef } from "react";

// ── Species Evidence Section ──────────────────────────────────────────
// Renders one evidence card per species entered in "Positive Tested Species".
// The confirmation checkbox is the only mandatory part - submission is
// blocked until every species is confirmed. Attaching actual proof (a
// file or link) is optional, shown collapsed behind "+ Add proof
// (optional)" unless a draft already has one attached.
//
// Props:
//   speciesString  — the raw value of formData.positive_tested_species
//   evidence       — array of evidence objects (stored in parent formData)
//   onChange       — (newEvidence) => void  — updates parent state
//
// Evidence object shape:
//   { species, type: "file"|"link", file: File|null, fileName, linkUrl, linkLabel, confirmed }

const COLORS = {
  primary:  "#305DBD",
  palest:   "#E1E9FC",
  mint:     "#3EB489",
  border:   "#D4D8DF",
  ink:      "#14161B",
  red:      "#dc2626",
  redLight: "#fef2f2",
  redBorder:"#fca5a5",
  amber:    "#fefce8",
  amberBorder: "#fde68a",
};

// Comma/semicolon are what the manual-entry placeholder suggests typing
// ("e.g., Human, Rat"); "|" is what the app's own CSV export converts
// positive_tested_species to (same convention as Applications/
// Collections - see generator.js), so a re-uploaded exported CSV needs
// it recognized too. All three keep this one exported function in sync
// instead of drifting - see DepositorForm.js's two other uses of it.
export function parseSpecies(raw) {
  if (!raw || !raw.trim()) return [];
  return raw
    .split(/[,;|]+/)
    .map(s => s.trim())
    .filter(Boolean);
}

export default function SpeciesEvidenceSection({ speciesString, evidence = [], onChange }) {
  const prevSpeciesRef = useRef([]);

  // Sync evidence cards whenever the species list changes.
  // Add a blank card for new species, remove cards for deleted species.
  useEffect(() => {
    const species = parseSpecies(speciesString);
    const prev    = prevSpeciesRef.current;

    if (JSON.stringify(species) === JSON.stringify(prev)) return;
    prevSpeciesRef.current = species;

    const updated = species.map(sp => {
      const existing = evidence.find(e => e.species === sp);
      return existing || {
        species:          sp,
        type:             "file",   // default to file upload
        file:             null,
        fileName:         "",
        evidenceTempKey:  "",   // set once the file finishes uploading to the server cache
        filePreviewUrl:   "",   // local blob URL for "open in new tab" from the Draft Entries table
        linkUrl:          "",
        linkLabel:        "",
        confirmed:        false,
      };
    });

    onChange(updated);
  }, [speciesString]); // eslint-disable-line

  const updateCard = (index, patch) => {
    const updated = evidence.map((e, i) => i === index ? { ...e, ...patch } : e);
    onChange(updated);
  };

  const species = parseSpecies(speciesString);
  if (species.length === 0) return null;

  return (
    <div className="full-width-field" style={{ maxWidth: "760px" }}>
      <label>Reactivity Evidence</label>
      {/* Section header */}
      <div style={{
        display: "flex", alignItems: "flex-start", gap: "10px",
        padding: "14px 16px", marginBottom: "12px",
        background: COLORS.palest, border: `1px solid ${COLORS.border}`,
        borderLeft: `4px solid ${COLORS.primary}`, borderRadius: "8px",
      }}>
        <span style={{ fontSize: "18px", flexShrink: 0 }}>🔬</span>
        <div>
          <p style={{ margin: "0 0 2px", fontSize: "13px", fontWeight: 700, color: COLORS.primary }}>
            Confirm Reactivity
          </p>
          <p style={{ margin: 0, fontSize: "12px", color: "#374151", lineHeight: 1.5 }}>
            For each species listed under Positive Tested Species, confirm your lab has observed
            reactivity — required before submission. Attaching proof (an image, document, or
            published paper/journal link) is optional.
          </p>
        </div>
      </div>

      {/* One card per species */}
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {evidence.map((ev, idx) => (
          <EvidenceCard
            key={ev.species}
            ev={ev}
            index={idx}
            onChange={patch => updateCard(idx, patch)}
          />
        ))}
      </div>
    </div>
  );
}

function EvidenceCard({ ev, onChange }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // Starts expanded if this draft already has proof attached (editing an
  // existing entry), collapsed otherwise - once opened for a card it
  // stays open, no need to re-hide it.
  const [showProof, setShowProof] = useState(!!(ev.fileName || ev.linkUrl?.trim()));

  const isComplete = ev.confirmed;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    // Accept: images, PDFs, Word, Excel, text — basically everything
    const allowedTypes = [
      "image/jpeg","image/png","image/gif","image/tiff","image/webp","image/bmp",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/plain","text/csv",
    ];
    if (!allowedTypes.includes(file.type)) {
      alert("Unsupported file type. Please upload an image, PDF, Word, or Excel document.");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      alert("File must be under 20MB.");
      return;
    }

    // Upload immediately — a raw File object can't survive a draft being saved to
    // localStorage, so we cache the bytes server-side now and just carry the temp
    // key (a plain string) through the draft's saved state until final submission.
    setUploading(true);
    try {
      const tempKey = `evidence_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const payload = new FormData();
      payload.append("evidence_file", file);
      payload.append("temp_key", tempKey);
      const res = await fetch("https://dshb-deposits.its.uiowa.edu/api/upload-evidence-file", {
        method: "POST", body: payload,
      });
      if (!res.ok) throw new Error();
      onChange({
        file: null,
        fileName: file.name,
        evidenceTempKey: tempKey,
        filePreviewUrl: URL.createObjectURL(file),
      });
    } catch {
      alert(`Failed to upload "${file.name}". Please try again.`);
      onChange({ file: null, fileName: "", evidenceTempKey: "", filePreviewUrl: "" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{
      border: `1.5px solid ${isComplete ? COLORS.mint : COLORS.border}`,
      borderRadius: "10px", background: "#fff",
      overflow: "hidden",
      boxShadow: isComplete ? "0 0 0 3px rgba(62,180,137,0.12)" : "none",
      transition: "border-color 0.2s, box-shadow 0.2s",
    }}>
      {/* Card header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 16px",
        background: isComplete ? "rgba(62,180,137,0.06)" : "#f8fafc",
        borderBottom: `1px solid ${COLORS.border}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "14px" }}>
            {isComplete ? "✅" : "⚠️"}
          </span>
          <span style={{ fontWeight: 700, fontSize: "14px", color: COLORS.ink }}>
            {ev.species}
          </span>
          {!isComplete && (
            <span style={{
              fontSize: "10px", fontWeight: 700, padding: "2px 8px",
              background: COLORS.amberBorder, color: "#92400e",
              borderRadius: "20px",
            }}>Confirmation required</span>
          )}
        </div>
        {isComplete && (
          <span style={{ fontSize: "11px", color: COLORS.mint, fontWeight: 700 }}>
            ✓ Confirmed
          </span>
        )}
      </div>

      <div style={{ padding: "14px 16px" }}>
        {/* Confirmation checkbox - the one required action */}
        <label style={{
          display: "flex", alignItems: "flex-start", gap: "10px",
          cursor: "pointer", fontSize: "13px",
          padding: "10px 12px",
          background: ev.confirmed ? "rgba(62,180,137,0.06)" : COLORS.amberBorder + "40",
          border: `1px solid ${ev.confirmed ? COLORS.mint : COLORS.amberBorder}`,
          borderRadius: "7px",
        }}>
          <input
            type="checkbox"
            checked={ev.confirmed}
            onChange={e => onChange({ confirmed: e.target.checked })}
            style={{ marginTop: "2px", flexShrink: 0, width: "15px", height: "15px" }}
          />
          <span style={{ color: COLORS.ink, lineHeight: 1.5 }}>
            <strong>I confirm</strong> that our lab has observed reactivity of this antibody
            in <strong>{ev.species}</strong>.
          </span>
        </label>

        {/* Optional proof - collapsed until asked for */}
        {!showProof ? (
          <button
            type="button"
            onClick={() => setShowProof(true)}
            style={{
              marginTop: "10px", background: "none", border: "none",
              cursor: "pointer", color: COLORS.primary, fontSize: "12px",
              fontWeight: 600, padding: 0,
            }}
          >
            + Add proof (optional)
          </button>
        ) : (
        <div style={{ marginTop: "14px" }}>
          <p style={{ margin: "0 0 8px", fontSize: "11px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Proof (optional)
          </p>
          {/* Evidence type toggle */}
          <div style={{ display: "flex", gap: "6px", marginBottom: "12px" }}>
            {[
              { value: "file", label: "📎 Upload file" },
              { value: "link", label: "🔗 Link / URL" },
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange({ type: opt.value, file: null, fileName: "", evidenceTempKey: "", filePreviewUrl: "", linkUrl: "", linkLabel: "" })}
                style={{
                  padding: "6px 14px", fontSize: "12px", fontWeight: 600,
                  border: `1.5px solid ${ev.type === opt.value ? COLORS.primary : COLORS.border}`,
                  borderRadius: "20px", cursor: "pointer",
                  background: ev.type === opt.value ? COLORS.palest : "#fff",
                  color: ev.type === opt.value ? COLORS.primary : "#6b7280",
                  transition: "all 0.15s",
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* File upload */}
        {ev.type === "file" && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
              style={{ display: "none" }}
              onChange={handleFile}
            />
            {uploading ? (
              <div style={{
                display: "flex", alignItems: "center", gap: "10px",
                padding: "10px 14px", background: "#f8fafc",
                border: `1px solid ${COLORS.border}`, borderRadius: "7px",
              }}>
                <span style={{ fontSize: "13px", color: "#6b7280" }}>Uploading…</span>
              </div>
            ) : ev.fileName && ev.evidenceTempKey ? (
              <div style={{
                display: "flex", alignItems: "center", gap: "10px",
                padding: "10px 14px", background: "#f0fdf4",
                border: "1px solid #86efac", borderRadius: "7px",
              }}>
                <span style={{ fontSize: "18px" }}>📄</span>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "#15803d", flex: 1 }}>
                  {ev.fileName}
                </span>
                <button
                  type="button"
                  onClick={() => onChange({ file: null, fileName: "", evidenceTempKey: "", filePreviewUrl: "" })}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    color: "#6b7280", fontSize: "13px", textDecoration: "underline",
                  }}
                >Remove</button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                style={{
                  width: "100%", padding: "20px 16px", cursor: "pointer",
                  border: `2px dashed ${COLORS.border}`, borderRadius: "8px",
                  background: "#fafbfc", color: "#6b7280", fontSize: "13px",
                  display: "flex", flexDirection: "column", alignItems: "center", gap: "6px",
                  transition: "border-color 0.15s, background 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.primary; e.currentTarget.style.background = COLORS.palest; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.background = "#fafbfc"; }}
              >
                <span style={{ fontSize: "24px" }}>📁</span>
                <span style={{ fontWeight: 600 }}>Click to upload evidence</span>
                <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                  Image, PDF, Word, Excel — up to 20MB
                </span>
              </button>
            )}
          </div>
        )}

        {/* Link / URL */}
        {ev.type === "link" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <input
              type="url"
              placeholder="https://pubmed.ncbi.nlm.nih.gov/... or DOI or any URL"
              value={ev.linkUrl || ""}
              onChange={e => onChange({ linkUrl: e.target.value })}
              style={{
                width: "100%", padding: "9px 12px", fontSize: "13px",
                border: `1.5px solid ${COLORS.border}`, borderRadius: "7px",
                fontFamily: "inherit", boxSizing: "border-box",
              }}
              onFocus={e => e.target.style.borderColor = COLORS.primary}
              onBlur={e => e.target.style.borderColor = COLORS.border}
            />
            <input
              type="text"
              placeholder="Label (optional) — e.g. 'Smith et al. 2023, Nature'"
              value={ev.linkLabel || ""}
              onChange={e => onChange({ linkLabel: e.target.value })}
              style={{
                width: "100%", padding: "9px 12px", fontSize: "13px",
                border: `1.5px solid ${COLORS.border}`, borderRadius: "7px",
                fontFamily: "inherit", boxSizing: "border-box",
              }}
              onFocus={e => e.target.style.borderColor = COLORS.primary}
              onBlur={e => e.target.style.borderColor = COLORS.border}
            />
          </div>
        )}
        </div>
        )}
      </div>
    </div>
  );
}